// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — where the highest and lowest values sit on a value-encoded release
 * frame, for the Data tab's "Mark highest & lowest".
 *
 * `datasetStats.findExtremum` cannot be used: it decodes linearly over the
 * whole frame, and a release keeps a calibration strip under its map (its
 * `dataRegion`) and may be log-scaled or classified. So this walks only the
 * map's rectangle and decodes each code by the release's own rules
 * (`valueAtCode`), once per code rather than once per texel: one pass marks
 * which codes occur, the extremes are chosen among those, and a second pass
 * finds the first texel carrying each (row-major, so a broad plateau gives
 * the same point every time rather than looking like drift).
 *
 * AYNI (owner, 2026-10-18): a pin that follows the extreme passes where it
 * stands as `near`, and gets the texel carrying the extreme nearest it, so
 * it stays with its patch frame after frame instead of jumping to whichever
 * tie the scan meets first (`followExtremes.ts`).
 *
 * The whole frame is read, so call this from a press, or for following pins
 * within their budget (`createLocateBudget`), never from a pointer handler
 * (see `GlLumaSampler.snapshot`).
 */

import type { DatasetOverlayOptions } from '../../types'
import type { LumaSnapshot } from '../glLumaSampler'
import { releaseCropRect, valueAtCode, type ReleaseEncoding } from '../dashRelease'
import { texelUvToLatLon } from '../datasetProbe'
import { arcDeg, type LatLon } from './followExtremes'
import { gridOf, type MaskGrid } from './surfaceMask'

export interface ReleaseExtreme {
  readonly lat: number
  readonly lon: number
  readonly value: number
}

/** The map's rectangle in a release frame of `width` × `height` (its calibration strip left out), and each texel's place. */
function mapBox(width: number, height: number, enc: ReleaseEncoding, options?: DatasetOverlayOptions) {
  const crop = options?.cropRect ?? releaseCropRect(enc)
  const box = {
    x0: Math.max(0, Math.floor(crop.u0 * width)),
    y0: Math.max(0, Math.floor(crop.v0 * height)),
    x1: Math.min(width, Math.ceil((crop.u0 + crop.us) * width)),
    y1: Math.min(height, Math.ceil((crop.v0 + crop.vs) * height)),
  }
  // Texel centre in the frame, then into the map's own 0..1 space.
  const placeOf = (x: number, y: number): LatLon => texelUvToLatLon({
    u: ((x + 0.5) / width - crop.u0) / crop.us,
    v: ((y + 0.5) / height - crop.v0) / crop.vs,
  }, options)
  return { box, placeOf }
}

/** AYNI (owner, 2026-10-18): a release frame's map as a grid for the land and sea mask (`surfaceMask.ts`), indexed like the frame. */
export function releaseGrid(width: number, height: number, enc: ReleaseEncoding, options?: DatasetOverlayOptions): MaskGrid | null {
  const { box, placeOf } = mapBox(width, height, enc, options)
  return box.x1 > box.x0 && box.y1 > box.y0 ? gridOf(width, height, box, placeOf) : null
}

/**
 * `allowed` (AYNI, owner 2026-10-18): one byte per texel of the frame, 0
 * where the extremes are not looked for (the sea, under "Land only").
 */
export function findReleaseExtremes(
  snapshot: LumaSnapshot,
  enc: ReleaseEncoding,
  options?: DatasetOverlayOptions,
  near?: { readonly max?: LatLon | null; readonly min?: LatLon | null },
  allowed?: Uint8Array | null,
): { max: ReleaseExtreme; min: ReleaseExtreme } | null {
  const { data, width, height } = snapshot
  if (!width || !height) return null
  const { box: { x0, y0, x1, y1 }, placeOf } = mapBox(width, height, enc, options)
  if (x1 <= x0 || y1 <= y0) return null
  const ok = allowed && allowed.length === width * height ? allowed : null
  if (allowed && !ok) return null

  const seen = new Uint8Array(256)
  for (let y = y0; y < y1; y++) {
    const row = y * width
    for (let x = x0; x < x1; x++) if (!ok || ok[row + x]) seen[data[row + x]] = 1
  }
  let maxCode = -1
  let minCode = -1
  let maxValue = -Infinity
  let minValue = Infinity
  for (let code = 0; code < 256; code++) {
    if (!seen[code]) continue
    const value = valueAtCode(enc, code)
    if (value === null) continue
    if (value > maxValue) { maxValue = value; maxCode = code }
    if (value < minValue) { minValue = value; minCode = code }
  }
  if (maxCode < 0) return null

  const locate = (code: number, value: number, to: LatLon | null | undefined): ReleaseExtreme => {
    let best: LatLon | null = null
    let bestD = Infinity
    for (let y = y0; y < y1; y++) {
      const row = y * width
      for (let x = x0; x < x1; x++) {
        if (data[row + x] !== code || (ok && !ok[row + x])) continue
        if (!to) return { ...placeOf(x, y), value }
        const at = placeOf(x, y)
        const d = arcDeg(at, to)
        if (d < bestD) { bestD = d; best = at }
      }
    }
    // The fallback is unreachable: the code was seen inside this rectangle, where allowed.
    return { ...(best ?? { lat: 0, lon: 0 }), value }
  }
  return { max: locate(maxCode, maxValue, near?.max), min: locate(minCode, minValue, near?.min) }
}
